/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  FocusEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {
  Account,
  GenericObject,
  SSOConfiguration
} from '@ion/core/src/types/entities';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import EditorDialog from '@ion/dls/src/editors/EditorDialog';
import styles from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import SSOApps, {Entry} from './SSOApps';
import {
  deserialize,
  deserializeSSOApps,
  getInitialState,
  serialize,
  SerializedArgs,
  setSSODomain,
  SSOFormFields,
  TargetType,
  validateSSOApps
} from './SSOEditorUtil';
import SSOInfo from './SSOInfo';

export interface SSOEditorProps {
  onClose?: (event: SyntheticEvent<{}, Event>) => void;
  onSave: (ssoConfiguration: SSOConfiguration) => void;
  refreshInitialization?: Function;
  ssoConfiguration?: SSOConfiguration;
  selectedAccount?: Account;
  isLoading?: boolean;
  clear: boolean;
};

const overrides = () => ({
  overrides : {
    marginTop : '16px',
    padding   : '0 16px'
  },
  flexStyle : {
    display : 'flex'
  },
  formActions : {
    borderTop      : `solid 2px ${dlsColor('grey5')}`,
    display        : 'flex',
    justifyContent : 'flex-end',
    bottom         : '0px'
  },
  fieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  },
  appStyle : {
    display   : 'flex',
    marginTop : '14px'
  },
  errorMessage : {
    ...dlsTypography('body3'),
    color       : dlsColor('caRed'),
    fontSize    : '12px !important',
    paddingLeft : '16px !important'
  }
});

/**
 * Common editor for the add / edit of a SSO Configuration.
 *
 * ```typescript
 *
 *    import 'SSOEditor' from '@ion/applications/src/pages/admin/accounts/SSOEditor';
 *
 *    <SSOEditor account={account}
 *      defaultAccount={globalGroup}
 *      onSave={onSave}
 *      onClose={onClose}
 *      loading={loading} />
 *
 * ```
 */
export const SSOEditor = ({
  onSave,
  onClose,
  selectedAccount,
  ssoConfiguration = {},
  clear = false,
  isLoading = false
}: SSOEditorProps) => {
  const editorStyles                = useExtendStyles(styles, overrides);
  const classes                     = editorStyles();
  const deserializedApp             = deserializeSSOApps(ssoConfiguration?.apps);
  const isEditMode                  = !isObjectEmpty(ssoConfiguration);
  const [ssoAppInfo, setSSOAppInfo] = useState<Entry[]>(deserializedApp);
  const [formState, setFormState]   = useFormState(getInitialState(ssoConfiguration, selectedAccount));

  useEffect(() => {
    if (isEditMode && ssoConfiguration?.id) {
      setFormState(deserialize(ssoConfiguration, selectedAccount));
    }
  }, [ssoConfiguration, isEditMode]);

  useEffect(() => {
    if (clear) {
      clearForm();
      setSSOAppInfo(null);
    }
  }, [clear]);

  const dialogTitle = useMemo(() => {
    const entry = isEditMode ? 'edit' : 'add';
    return translate(`common:editors.shared.${entry}`, {
      entityLabel : translate('admin-groups:ssoConfig.title')
    });
  }, [isEditMode]);

  const clearForm = useCallback(function() {
    setFormState({}, true);
  }, []);

  const debounceFieldChange = useCallback(debounce((target: TargetType) => {
    const {name, value, required = false} = target;
    setFormState({[name]: {name, value, required}});
  }, 400), []);

  const handleFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist?.();
    debounceFieldChange(e.target);
  }, []);

  const handleChooserChange = useCallback(({name, value, required = false}) => {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
  }, []);

  const onCancelDialog = useCallback(function(e): void {
    clearForm();
    onClose?.(e);
  }, [onClose]);

  const submitValidation = useMemo(() => ({
    [SSOFormFields.APPs] : () => {
      const {isValid, appList} = validateSSOApps(ssoAppInfo);
      setSSOAppInfo(appList);
      return isValid;
    },
    [SSOFormFields.Domains] : () => {
      const domain  = formState[SSOFormFields.Domains]?.value;
      const isValid = setSSODomain(domain, setFormState);
      return !isValid;
    }
  }), [ssoAppInfo, formState[SSOFormFields.Domains]]);

  const serializer = function(state: FormState): GenericObject {
    return serialize({state, accountId: selectedAccount?.id} as unknown as SerializedArgs);
  };

  const handleSubmit = useFormSave({
    validate  : submitValidation,
    serialize : serializer,
    onSave    : onSave,
    formState,
    setFormState
  });

  if (formState[SSOFormFields.DiscoveryEndpoint]?.error) {
    smoothScrollByElement('editor');
  }
  
  const validateSODomain = useCallback(debounce(({value = null}) => (
    setSSODomain(value, setFormState)
  ), 400), [formState[SSOFormFields.Domains]]);

  const handleBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    e.persist?.();
    validateSODomain(e.target);
  }, [formState]);

  return (
    <EditorDialog open
      dialogTitle={dialogTitle}
      onCloseDialog={onClose}
      onCancelDialog={onCancelDialog}
      onPrimaryButtonClick={handleSubmit}
      primaryButtonLoading={isLoading}>
      <form id={'editor'} className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.formWrapper}>
          <SSOInfo handleChooserChange={handleChooserChange}
            handleFieldChange={handleFieldChange}
            onBlur={handleBlur}
            selectedAccount={selectedAccount}
            formState={formState} />
          <SSOApps ssoAppInfo={ssoAppInfo}
            onChange={function(value) {
              setSSOAppInfo(value);
              handleChooserChange({
                name  : SSOFormFields.APPs,
                value : value
              });
            }}
            minApp={1} />
        </div>
      </form>
    </EditorDialog>
  );
};

export default SSOEditor;
